import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, Link2, Receipt } from "lucide-react";
import type { Referral, PartnerReferrer, Deal } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

interface PartnerReferralsPanelProps {
  partnerId: string;
}

interface ReferralFormData {
  referralDate: string;
  partnerReferrerId: string;
  referralValue: string;
  referralStatus: string;
  commissionPaid: string;
  commissionDueDate: string;
  commissionPaidDate: string;
  notes: string;
}

const defaultFormData: ReferralFormData = {
  referralDate: new Date().toISOString().split("T")[0],
  partnerReferrerId: "",
  referralValue: "",
  referralStatus: "new",
  commissionPaid: "",
  commissionDueDate: "",
  commissionPaidDate: "",
  notes: "",
};

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" },
  { value: "paid", label: "Paid" },
];

const STATUS_BADGE_CLASSES: Record<string, string> = {
  new: "bg-gray-100 text-gray-800 hover:bg-gray-100",
  converted: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  lost: "bg-red-100 text-red-800 hover:bg-red-100",
  paid: "bg-green-100 text-green-800 hover:bg-green-100",
};

interface ReferralWithReferrer extends Referral {
  referrerName?: string;
  dealName?: string;
}

export function PartnerReferralsPanel({ partnerId }: PartnerReferralsPanelProps) {
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [isLinkDealOpen, setIsLinkDealOpen] = useState(false);
  const [editingReferral, setEditingReferral] = useState<ReferralWithReferrer | null>(null);
  const [linkingReferral, setLinkingReferral] = useState<ReferralWithReferrer | null>(null);
  const [formData, setFormData] = useState<ReferralFormData>(defaultFormData);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [deleteReferral, setDeleteReferral] = useState<ReferralWithReferrer | null>(null);
  const { toast } = useToast();

  // Fetch referrals for this partner (uses default queryFn with auth)
  const { data: referrals = [], isLoading } = useQuery<Referral[]>({
    queryKey: ["/api/crm/partners", partnerId, "referrals"],
  });

  // Fetch referrers for dropdown (uses default queryFn with auth)
  const { data: referrers = [] } = useQuery<PartnerReferrer[]>({
    queryKey: ["/api/crm/partners", partnerId, "referrers"],
  });

  // Fetch all deals for linking (uses default queryFn with auth)
  const { data: deals = [] } = useQuery<Deal[]>({
    queryKey: ["/api/crm/deals"],
  });

  // Enrich referrals with referrer and deal names
  const enrichedReferrals: ReferralWithReferrer[] = useMemo(() => {
    return referrals.map((referral) => {
      const referrer = referrers.find((r) => r.id === referral.partnerReferrerId);
      const deal = deals.find((d) => d.id === referral.dealId);
      return {
        ...referral,
        referrerName: referrer ? `${referrer.firstName} ${referrer.lastName}` : undefined,
        dealName: deal?.dealName || undefined,
      };
    });
  }, [referrals, referrers, deals]);

  // Sort referrals by date descending
  const sortedReferrals = useMemo(() => {
    return [...enrichedReferrals].sort((a, b) => {
      const dateA = new Date(a.referralDate).getTime();
      const dateB = new Date(b.referralDate).getTime();
      return dateB - dateA;
    });
  }, [enrichedReferrals]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<ReferralFormData>) => {
      const response = await apiRequest("POST", `/api/crm/partners/${partnerId}/referrals`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrals`] });
      toast({ title: "Referral added successfully" });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add referral",
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ReferralFormData> }) => {
      const response = await apiRequest("PATCH", `/api/crm/referrals/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrals`] });
      toast({ title: "Referral updated successfully" });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update referral",
        variant: "destructive",
      });
    },
  });

  // Link deal mutation
  const linkDealMutation = useMutation({
    mutationFn: async ({ id, dealId }: { id: string; dealId: string }) => {
      const response = await apiRequest("PATCH", `/api/crm/referrals/${id}/link-deal`, { dealId });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrals`] });
      toast({ title: "Deal linked successfully" });
      handleCloseLinkDeal();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to link deal",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/referrals/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/partners/${partnerId}/referrals`] });
      toast({ title: "Referral deleted" });
      setDeleteReferral(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete referral",
        variant: "destructive",
      });
    },
  });

  const handleCloseDialog = () => {
    setIsAddEditOpen(false);
    setEditingReferral(null);
    setFormData(defaultFormData);
  };

  const handleCloseLinkDeal = () => {
    setIsLinkDealOpen(false);
    setLinkingReferral(null);
    setSelectedDealId("");
  };

  const handleOpenAdd = () => {
    setEditingReferral(null);
    setFormData({
      ...defaultFormData,
      referralDate: new Date().toISOString().split("T")[0],
    });
    setIsAddEditOpen(true);
  };

  const handleOpenEdit = (referral: ReferralWithReferrer) => {
    setEditingReferral(referral);
    setFormData({
      referralDate: referral.referralDate,
      partnerReferrerId: referral.partnerReferrerId || "",
      referralValue: referral.referralValue || "",
      referralStatus: referral.referralStatus || "new",
      commissionPaid: referral.commissionPaid || "",
      commissionDueDate: referral.commissionDueDate || "",
      commissionPaidDate: referral.commissionPaidDate || "",
      notes: referral.notes || "",
    });
    setIsAddEditOpen(true);
  };

  const handleOpenLinkDeal = (referral: ReferralWithReferrer) => {
    setLinkingReferral(referral);
    setSelectedDealId("");
    setIsLinkDealOpen(true);
  };

  const handleSubmit = () => {
    const submitData: Record<string, string | undefined> = {
      referralDate: formData.referralDate,
      partnerReferrerId: formData.partnerReferrerId || undefined,
      referralValue: formData.referralValue || undefined,
      referralStatus: formData.referralStatus || undefined,
      notes: formData.notes || undefined,
    };

    // Add edit-only fields
    if (editingReferral) {
      submitData.commissionPaid = formData.commissionPaid || undefined;
      submitData.commissionDueDate = formData.commissionDueDate || undefined;
      submitData.commissionPaidDate = formData.commissionPaidDate || undefined;
    }

    // Clean undefined values
    const cleanedData = Object.fromEntries(
      Object.entries(submitData).filter(([_, v]) => v !== undefined)
    );

    if (editingReferral) {
      updateMutation.mutate({ id: editingReferral.id, data: cleanedData });
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const handleLinkDeal = () => {
    if (linkingReferral && selectedDealId) {
      linkDealMutation.mutate({ id: linkingReferral.id, dealId: selectedDealId });
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value: string | null | undefined): string => {
    if (!value) return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const isFormValid = formData.referralDate !== "";
  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isLinking = linkDealMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="panel-partner-referrals">
      {/* Panel Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">
          REFERRALS ({referrals.length})
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={handleOpenAdd}
          data-testid="btn-add-referral"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Referral
        </Button>
      </div>

      {/* Empty State */}
      {referrals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md">
          <Receipt className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No referrals recorded</p>
          <p className="text-xs text-muted-foreground mt-1">
            Track referrals and commissions from this partner
          </p>
        </div>
      ) : (
        /* Referrals Table */
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Referred By</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Deal</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Value</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Commission</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedReferrals.map((referral) => (
                <TableRow key={referral.id} className="h-10">
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(referral.referralDate)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {referral.referrerName || "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {referral.dealName || "No Deal"}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatCurrency(referral.referralValue)}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge
                      variant="secondary"
                      className={STATUS_BADGE_CLASSES[referral.referralStatus || "new"]}
                      data-testid={`badge-status-${referral.referralStatus || "new"}`}
                    >
                      {referral.referralStatus || "new"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatCurrency(referral.commissionPaid)}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(referral)}
                        data-testid={`btn-edit-referral-${referral.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!referral.dealId && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenLinkDeal(referral)}
                          data-testid={`btn-link-deal-${referral.id}`}
                          aria-label="Link Deal"
                        >
                          <Link2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteReferral(referral)}
                        data-testid={`btn-delete-referral-${referral.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddEditOpen} onOpenChange={setIsAddEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingReferral ? "Edit Referral" : "Add Referral"}
            </DialogTitle>
            <DialogDescription>
              {editingReferral
                ? "Update the referral details."
                : "Add a new referral for this partner."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Referral Date */}
            <div className="space-y-2">
              <Label htmlFor="referralDate">Referral Date *</Label>
              <Input
                id="referralDate"
                type="date"
                value={formData.referralDate}
                onChange={(e) => setFormData({ ...formData, referralDate: e.target.value })}
                aria-label="Referral Date"
              />
            </div>

            {/* Referred By */}
            <div className="space-y-2">
              <Label htmlFor="referredBy">Referred By</Label>
              <Select
                value={formData.partnerReferrerId}
                onValueChange={(value) => setFormData({ ...formData, partnerReferrerId: value })}
              >
                <SelectTrigger id="referredBy" aria-label="Referred By">
                  <SelectValue placeholder="Select referrer" />
                </SelectTrigger>
                <SelectContent>
                  {referrers.map((referrer) => (
                    <SelectItem key={referrer.id} value={referrer.id}>
                      {referrer.firstName} {referrer.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value */}
            <div className="space-y-2">
              <Label htmlFor="referralValue">Estimated Value</Label>
              <Input
                id="referralValue"
                type="number"
                value={formData.referralValue}
                onChange={(e) => setFormData({ ...formData, referralValue: e.target.value })}
                placeholder="500000"
                aria-label="Value"
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="referralStatus">Status</Label>
              <Select
                value={formData.referralStatus}
                onValueChange={(value) => setFormData({ ...formData, referralStatus: value })}
              >
                <SelectTrigger id="referralStatus" aria-label="Status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Commission fields (edit only) */}
            {editingReferral && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="commissionPaid">Commission Paid</Label>
                  <Input
                    id="commissionPaid"
                    type="number"
                    value={formData.commissionPaid}
                    onChange={(e) => setFormData({ ...formData, commissionPaid: e.target.value })}
                    placeholder="10000"
                    aria-label="Commission Paid"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="commissionDueDate">Commission Due Date</Label>
                  <Input
                    id="commissionDueDate"
                    type="date"
                    value={formData.commissionDueDate}
                    onChange={(e) => setFormData({ ...formData, commissionDueDate: e.target.value })}
                    aria-label="Commission Due Date"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="commissionPaidDate">Commission Paid Date</Label>
                  <Input
                    id="commissionPaidDate"
                    type="date"
                    value={formData.commissionPaidDate}
                    onChange={(e) => setFormData({ ...formData, commissionPaidDate: e.target.value })}
                    aria-label="Commission Paid Date"
                  />
                </div>
              </>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this referral..."
                rows={3}
                aria-label="Notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Deal Dialog */}
      <Dialog open={isLinkDealOpen} onOpenChange={setIsLinkDealOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link to Deal</DialogTitle>
            <DialogDescription>
              Select a deal to link to this referral.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dealSelect">Deal</Label>
              <Select
                value={selectedDealId}
                onValueChange={setSelectedDealId}
              >
                <SelectTrigger id="dealSelect" aria-label="Deal">
                  <SelectValue placeholder="Select a deal" />
                </SelectTrigger>
                <SelectContent>
                  {deals.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>
                      {deal.dealName || `Deal ${deal.id.slice(0, 8)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseLinkDeal}>
              Cancel
            </Button>
            <Button
              onClick={handleLinkDeal}
              disabled={!selectedDealId || isLinking}
            >
              {isLinking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteReferral}
        onOpenChange={(open) => !open && setDeleteReferral(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-referral">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Referral</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this referral? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteReferral && deleteMutation.mutate(deleteReferral.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
